/**
 * Shared with me (part 38c; decision 75; tables JL and CC; cockatrice-ui `Library.jsx`'s Shared with me): the books
 * shared with the writer as covers, each opening its read view; the waiting invite's card (accept it, or the account's
 * cards with the invite's words when there is no account here); what the last invite link was (incomplete, accounts
 * off); the offline and unavailable notes; and, for a guest who never started a trial, the way to start one (IN18).
 */
import { createSignal } from 'solid-js';
import type { Accessor } from 'solid-js';
import { isGuest } from '../account/status.ts';
import { fieldsOf, signedCopyOf } from '../account/wire.ts';
import type { CachedClaim } from '../shared/store.ts';
import { acceptWords, dateWords } from './account-sheet.ts';
import { coverOf } from '../shell/shelf.ts';
import { each, el, when, whenSome } from '../ui/dom.ts';
import type { Child } from '../ui/dom.ts';
import Banner from '../ui/Banner.ts';
import BookCard from '../ui/BookCard.ts';
import BookRow from '../ui/BookRow.ts';
import Button from '../ui/Button.ts';
import Card from '../ui/Card.ts';
import EmptyState from '../ui/EmptyState.ts';
import Section from '../ui/Section.ts';
import { component } from '../ui/solid.ts';
import type { Shell } from './shell.ts';

/**
 * A shared book on the shelf (PLAN.md §3, step 5: the library's BookCard, a button: the address stays the app's): its
 * cover in the colour of its invite (the same on every device) with its title, the title, and the role and author as the
 * design's meta line; the end date and what keeps it from opening under the card. The whole card opens it. `claim` is
 * the claim of `invite` as last read: a check reads every claim anew, and the card (with the focus on it) stays,
 * following it.
 */
function SharedCard(shell: Shell, invite: string, claim: Accessor<CachedClaim>): Child {
  const { t } = shell;
  const summary = (): { readonly title: string; readonly author: string } | undefined => shell.shared.summaries().get(invite);
  const title = (): string => {
    const named = summary()?.title ?? '';
    return named === '' ? t('shared.untitled') : named;
  };
  // H9: the cover's colour from the book's ID (here its invite's: the same on every device), its title on it.
  const index = coverOf(invite);
  return [
    component(BookCard, {
      class: 'shared-book',
      get 'aria-label'() {
        return t('shared.open', { title: title() });
      },
      get title() {
        return title();
      },
      cover: { 'data-cover': String(index), bg: `var(--cover-${String(index)})`, ink: `var(--cover-${String(index)}-ink)`, overlayTitle: true },
      // The design's "Editor · by K. Nilsen".
      get note() {
        const author = summary()?.author ?? '';
        return el(
          'span',
          { class: 'shared-meta' },
          el('span', {}, () => t(`shared.role.${claim().role}`)),
          author === '' ? null : [' · ', el('span', {}, t('shared.by', { author }))],
        );
      },
      onClick: () => {
        shell.read(invite);
      },
    }),
    whenSome(
      () => {
        const { endsAt, person } = claim();
        const words = [
          ...(endsAt === null ? [] : [t('shared.ends', { date: dateWords(shell, endsAt) })]),
          ...(person === null ? [t('shared.finish')] : summary() === undefined ? [t('shared.waiting')] : []),
        ];
        return words.length === 0 ? null : words;
      },
      (words) => el('p', { class: 'sh-help shared-state' }, () => words().map((line) => el('span', {}, line))),
    ),
  ];
}

/** JL1–JL3: the waiting invite: Accept (logged in), or create an account or log in with the invite's words. */
function JoinCard(shell: Shell): Child {
  const { t, account, ui, shared } = shell;
  const loggedIn = (): boolean => {
    const status = account.status();
    return status.kind === 'account' && !status.signedOut;
  };
  const outcome = (): string => {
    const state = shared.invite();
    return state.kind !== 'outcome' || state.outcome.kind === 'accepted' ? '' : acceptWords(shell, state.outcome);
  };
  return when(
    () => shared.invite().kind === 'waiting' || (shared.invite().kind === 'outcome' && outcome() !== ''),
    () =>
      el(
        'section',
        { class: 'join-card', 'aria-labelledby': 'join-title' },
        component(Card, {
          titleAs: 'h2',
          title: el('span', { id: 'join-title' }, t('join.accept.title')),
          meta: el('span', { class: 'join-lead' }, () => (loggedIn() ? t('join.accept.lead') : t('join.create.lead'))),
          get children() {
            return when(
              () => outcome() !== '',
              () => component(Banner, { tone: 'danger', live: true, class: 'join-error', children: outcome }),
            );
          },
          footer: [
            when(
              loggedIn,
              () => component(Button, { variant: 'primary', class: 'join-accept', 'aria-haspopup': 'dialog', onClick: () => ui.setDialog({ kind: 'account', task: { kind: 'accept' } }), children: t('join.accept.button') }),
              () => [
                component(Button, { variant: 'primary', class: 'join-create', onClick: () => ui.setAccountStep('create'), children: t('join.create.title') }),
                component(Button, { class: 'join-login', onClick: () => ui.setAccountStep('login'), children: t('account.login.submit') }),
              ],
            ),
            component(Button, { variant: 'ghost', class: 'join-dismiss', onClick: () => void shared.dismiss(), children: t('join.accept.notNow') }),
          ],
        }),
      ),
  );
}

/** IN18: a guest who never started a trial: the way to start one on the same account. */
function TrialPanel(shell: Shell): Child {
  const { t, account } = shell;
  const [busy, setBusy] = createSignal(false);
  const [failed, setFailed] = createSignal(false);
  // A press while busy never comes here: the busy Button ignores it.
  const start = async (): Promise<void> => {
    setBusy(true);
    setFailed(false);
    const reply = await account.post('/v1/account/trial', {}, (value) => (signedCopyOf(fieldsOf(value)?.['trial']) === null ? null : true));
    setBusy(false);
    if (reply.kind !== 'ok') {
      setFailed(true);
      return;
    }
    // The new copy comes with the next refresh (C2 checks it), as every other copy does.
    await account.refresh();
  };
  return when(
    () => isGuest(account.status()),
    () =>
      el(
        'section',
        { class: 'shared-trial', 'aria-labelledby': 'trial-title' },
        component(Card, {
          titleAs: 'h2',
          title: el('span', { id: 'trial-title' }, t('shared.trial.title')),
          meta: el('span', { class: 'trial-lead' }, t('shared.trial.lead')),
          children: when(failed, () => component(Banner, { tone: 'danger', live: true, class: 'trial-error', children: t('shared.trial.failed') })),
          footer: component(Button, {
            variant: 'primary',
            class: 'start-guest-trial',
            // Busy keeps the button's words as its name and says so; a second press meanwhile does nothing.
            get loading() {
              return busy();
            },
            'aria-busy': 'false',
            onClick: () => void start(),
            children: t('shared.trial.start'),
          }),
        }),
      ),
  );
}

/** JL4, JL7: what the last invite link was, when it couldn't be kept. */
function LinkNote(shell: Shell): Child {
  const { t, shared } = shell;
  return whenSome(
    () => {
      const taken = shared.link();
      return taken === 'incomplete' || taken === 'off' ? taken : null;
    },
    (taken) => component(Banner, { tone: 'danger', live: true, class: 'join-link', children: () => (taken() === 'off' ? t('join.off') : t('join.incomplete')) }),
  );
}

/**
 * Part 38c (decision 75), deep-fix r5 (tenet 3): whether the home page holds only Shared with me: a guest who never
 * started a trial and has no writing of their own on this device. A guest's own books written before the account stay
 * shown, read only, their exports open; the empty first book a device makes at its first visit isn't writing.
 */
export function onlyShared(shell: Shell): boolean {
  const { workspace } = shell;
  return isGuest(shell.account.status()) && workspace.books().every((book) => (workspace.writing(book.id).words ?? 0) === 0);
}

/**
 * Shared with me: its notes and cards, then the books shared with the writer (H3, part 38c) on a shelf like My books
 * (the library's Section, BookRow and BookCard; an EmptyState while nothing is shared).
 */
export function SharedSection(shell: Shell): Child {
  const { t, shared } = shell;
  // No banner heads the page (no book open, or a guest's home holding only this): this heading does (2.4.6, axe's
  // page-has-heading-one).
  const top = (): boolean => onlyShared(shell) || shell.workspace.book() === null;
  return component(Section, {
    class: 'lib-section',
    id: 'home-shared',
    tabIndex: -1,
    get level() {
      return top() ? 1 : 2;
    },
    title: t('home.shared'),
    description: t('home.sharedHint'),
    headingId: 'shared-title',
    headingFocusable: true,
    actions: component(Button, {
      variant: 'ghost',
      size: 'sm',
      iconStart: 'refresh',
      class: 'shared-check',
      get disabled() {
        return shared.checking();
      },
      onClick: () => void shared.check(),
      get children() {
        return shared.checking() ? t('shared.checking') : t('shared.check');
      },
    }),
    children: el(
      'div',
      { class: 'shared-body' },
      LinkNote(shell),
      JoinCard(shell),
      when(shared.offline, () => component(Banner, { tone: 'info', live: true, class: 'shared-offline', children: t('shared.offline') })),
      when(
        () => shared.unavailable().length > 0,
        () => component(Banner, { tone: 'info', live: true, class: 'shared-unavailable', children: t('shared.unavailable') }),
      ),
      when(
        () => shared.claims().length === 0,
        () =>
          component(EmptyState, {
            size: 'inline',
            icon: 'people',
            class: 'shared-empty',
            get headingLevel() {
              return top() ? 2 : 3;
            },
            title: t('shared.emptyTitle'),
            children: t('shared.empty'),
          }),
        () =>
          component(BookRow, {
            class: 'shared-shelf',
            // One card per invite, kept by it (never by the claim read, which every check makes anew).
            children: each(
              () => shared.claims().map((claim) => claim.invite),
              (invite) =>
                whenSome(
                  () => shared.claims().find((claim) => claim.invite === invite),
                  (claim) =>
                    el(
                      'div',
                      { class: 'shared-item' },
                      SharedCard(shell, invite, claim),
                      // AS8 (KG11): its key not here (the keyring made on another device after this one logged in): the password opens it.
                      when(
                        () => claim().person === null,
                        () => component(Button, { size: 'sm', class: 'shared-unlock', 'aria-haspopup': 'dialog', onClick: () => { shell.ui.setDialog({ kind: 'account', task: { kind: 'unlock' } }); }, children: t('shared.unlock.button') }),
                      ),
                    ),
                ),
            ),
          }),
      ),
      TrialPanel(shell),
    ),
  });
}
